import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  Grid,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";

import SugerenciasModal from "./SugerenciasModal.js";
import ConciliacionManualModal from "./ConciliacionManualModal.js";
import { useResultadosProcesamiento } from "./hooks";
import { procesarDatosResultados, esModoMarchaBlanca } from "./utils";
import MetricasResumen from "./resultados/MetricasResumen.js";
import CardCategoria from "./resultados/CardCategoria.js";
import TablaMovimientos from "./resultados/TablaMovimientos.js";
import SeccionRecienConciliados from "./SeccionRecienConciliados.js";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.success.main,
    color: "#fff",
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: "#fff",
  },
  resultCard: {
    height: "100%",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  cardSuccess: {
    borderLeft: `4px solid ${theme.palette.success.main}`,
    backgroundColor: theme.palette.success.lighter || "#E8F5E9",
  },
  cardWarning: {
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    backgroundColor: theme.palette.warning.lighter || "#FFF3E0",
  },
  cardError: {
    borderLeft: `4px solid ${theme.palette.error.main}`,
    backgroundColor: theme.palette.error.lighter || "#FFEBEE",
  },
  cardNeutral: {
    borderLeft: `4px solid ${theme.palette.grey[400]}`,
    backgroundColor: theme.palette.grey[50],
  },
  cardIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
  },
  cardNumber: {
    fontSize: 32,
    fontWeight: "bold",
    marginBottom: theme.spacing(0.5),
  },
  cardLabel: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  cardDescription: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  expandSection: {
    marginTop: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
  },
  expandHeader: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "&:hover": {
      backgroundColor: theme.palette.grey[100],
    },
  },
  expandContent: {
    padding: theme.spacing(2),
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  actionButton: {
    marginLeft: theme.spacing(0.5),
  },
}));

export default function ModalResultadosProcesamiento({
  open,
  onClose,
  resultados,
}) {
  const classes = useStyles();
  // ==================== CUSTOM HOOK ====================
  const {
    // Estados
    expandedSections,
    pagination,
    procesandoAccion,
    modalSugerenciasOpen,
    modalConciliarOpen,

    // Handlers de expansión
    toggleSection,

    // Handlers de paginación
    handleChangePage,
    handleChangeRowsPerPage,

    // Handlers de modales auxiliares
    handleVerSugerencias,
    handleCloseSugerencias,
    handleConciliarManual,
    handleCloseConciliarManual,

    // Handlers de acciones
    handleConciliarPerfectos,
    handleConciliarIndividual,
    handleConciliacionExitosaDesdeModal,

    movimientosEnProceso,
    recienConciliados,
  } = useResultadosProcesamiento();

  if (!resultados) return null;

  // Procesar y clasificar datos enriquecidos
  const datosEnriquecidos = procesarDatosResultados(resultados);
  const { clasificados, metricas, porCategoria } = datosEnriquecidos;

  // Filtrar movimientos que ya fueron conciliados
  const filtrarConciliados = (items) => {
    const idsConciliados = new Set(
      recienConciliados.map((r) => r.movimiento_id),
    );
    return items.filter((item) => !idsConciliados.has(item.movimiento_id));
  };

  //  Aplicar filtro a cada categoría
  const clasificadosFiltrados = {
    perfectos: filtrarConciliados(clasificados.perfectos),
    buenos: filtrarConciliados(clasificados.buenos),
    debiles: filtrarConciliados(clasificados.debiles),
    sinMatch: filtrarConciliados(clasificados.sinMatch),
    errores: clasificados.errores,
  };

  // Determinar si estamos en modo marcha blanca
  const modoMarchaBlanca = esModoMarchaBlanca(clasificadosFiltrados.perfectos);

  return (
    <Dialog
      id="modal-resultados-procesamiento"
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" gap={1}>
          <CheckCircleIcon />
          <Typography variant="h6">Procesamiento Completado</Typography>
        </Box>
        <IconButton className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* ==================== MÉTRICAS RESUMEN ==================== */}
        <MetricasResumen
          metricas={metricas}
          porCategoria={porCategoria}
          modoMarchaBlanca={modoMarchaBlanca}
        />

        {/* SECCIÓN RECIÉN CONCILIADOS */}
        {recienConciliados.length > 0 && (
          <SeccionRecienConciliados
            recienConciliados={recienConciliados}
            expanded={expandedSections.recienConciliados}
            onToggle={() => toggleSection("recienConciliados")}
            pagination={pagination.recienConciliados}
            onChangePage={(newPage) =>
              handleChangePage("recienConciliados", newPage)
            }
            onChangeRowsPerPage={(event) =>
              handleChangeRowsPerPage("recienConciliados", event)
            }
          />
        )}

        {/* ==================== CARDS DE CATEGORÍAS ==================== */}
        <Grid container spacing={2} style={{ marginBottom: 24 }}>
          {/* Card Perfectos */}
          <CardCategoria
            tipo="perfectos"
            titulo={
              modoMarchaBlanca ? "🎯 Matches Perfectos" : "✅ Auto-conciliados"
            }
            descripcion={
              modoMarchaBlanca
                ? "Score ≥90 - Listos para aprobar"
                : "Score ≥90 - Ya procesados"
            }
            cantidad={porCategoria.perfectos.cantidad}
            monto={porCategoria.perfectos.monto}
            scorePromedio={porCategoria.perfectos.scorePromedio}
            icono={<CheckCircleIcon />}
            colorIcono="#4caf50"
            onClick={() => toggleSection("perfectos")}
            modoMarchaBlanca={modoMarchaBlanca}
          />

          {/* Card Buenos */}
          <CardCategoria
            tipo="buenos"
            titulo="🟡 Matches Buenos"
            descripcion="Score 70-89 - Revisar sugerencias"
            cantidad={porCategoria.buenos.cantidad}
            monto={porCategoria.buenos.monto}
            scorePromedio={porCategoria.buenos.scorePromedio}
            icono={<WarningIcon />}
            colorIcono="#ff9800"
            onClick={() => toggleSection("buenos")}
          />

          {/* Card Débiles (solo si existen) */}
          {porCategoria.debiles.cantidad > 0 && (
            <CardCategoria
              tipo="debiles"
              titulo="🟠 Matches Débiles"
              descripcion="Score 30-69 - Requiere revisión"
              cantidad={porCategoria.debiles.cantidad}
              monto={porCategoria.debiles.monto}
              scorePromedio={porCategoria.debiles.scorePromedio}
              icono={<InfoIcon />}
              colorIcono="#fb8c00"
              onClick={() => toggleSection("debiles")}
            />
          )}

          {/* Card Sin Match */}
          <CardCategoria
            tipo="sinMatch"
            titulo="🔴 Sin Match"
            descripcion="Score <30 - Conciliación manual"
            cantidad={porCategoria.sinMatch.cantidad}
            monto={porCategoria.sinMatch.monto}
            icono={<ErrorIcon />}
            colorIcono="#f44336"
            onClick={() => toggleSection("sinMatch")}
          />

          {/* Card Errores (solo si existen) */}
          {porCategoria.errores.cantidad > 0 && (
            <CardCategoria
              tipo="errores"
              titulo="⚪ Errores"
              descripcion="Movimientos con errores de procesamiento"
              cantidad={porCategoria.errores.cantidad}
              icono={<ErrorIcon />}
              colorIcono="#9e9e9e"
              onClick={() => toggleSection("errores")}
            />
          )}
        </Grid>

        {/* ==================== TABLAS EXPANDIBLES ==================== */}

        {/* Tabla Perfectos */}
        <TablaMovimientos
          sectionKey="perfectos"
          titulo={
            modoMarchaBlanca ? "🎯 Matches Perfectos" : "✅ Auto-conciliados"
          }
          icono={<CheckCircleIcon style={{ color: "#4caf50" }} />}
          cantidad={porCategoria.perfectos.cantidad}
          movimientos={clasificadosFiltrados.perfectos}
          bgColor="#E8F5E9"
          expanded={expandedSections.perfectos}
          onToggle={() => toggleSection("perfectos")}
          pagination={pagination.perfectos}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          showActions={modoMarchaBlanca}
          categoria="perfectos"
          onVerSugerencias={handleVerSugerencias}
          onConciliarManual={handleConciliarManual}
          onConciliarIndividual={(detalle) =>
            handleConciliarIndividual(detalle, onClose)
          }
          procesandoAccion={procesandoAccion}
          movimientosEnProceso={movimientosEnProceso}
        />

        {/* Tabla Buenos */}
        <TablaMovimientos
          sectionKey="buenos"
          titulo="🟡 Matches Buenos"
          icono={<WarningIcon style={{ color: "#ff9800" }} />}
          cantidad={porCategoria.buenos.cantidad}
          movimientos={clasificadosFiltrados.buenos}
          bgColor="#FFF3E0"
          expanded={expandedSections.buenos}
          onToggle={() => toggleSection("buenos")}
          pagination={pagination.buenos}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          showActions={true}
          categoria="buenos"
          onVerSugerencias={handleVerSugerencias}
          onConciliarManual={handleConciliarManual}
          onConciliarIndividual={(detalle) =>
            handleConciliarIndividual(detalle, onClose)
          }
          procesandoAccion={procesandoAccion}
          movimientosEnProceso={movimientosEnProceso}
        />

        {/* Tabla Débiles (solo si existen) */}
        {porCategoria.debiles.cantidad > 0 && (
          <TablaMovimientos
            sectionKey="debiles"
            titulo="🟠 Matches Débiles"
            icono={<InfoIcon style={{ color: "#fb8c00" }} />}
            cantidad={porCategoria.debiles.cantidad}
            movimientos={clasificadosFiltrados.debiles}
            bgColor="#FFF8E1"
            expanded={expandedSections.debiles}
            onToggle={() => toggleSection("debiles")}
            pagination={pagination.debiles}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            showActions={true}
            categoria="debiles"
            onVerSugerencias={handleVerSugerencias}
            onConciliarManual={handleConciliarManual}
            onConciliarIndividual={(detalle) =>
              handleConciliarIndividual(detalle, onClose)
            }
            procesandoAccion={procesandoAccion}
            movimientosEnProceso={movimientosEnProceso}
          />
        )}

        {/* Tabla Sin Match */}
        <TablaMovimientos
          sectionKey="sinMatch"
          titulo="🔴 Sin Match"
          icono={<ErrorIcon style={{ color: "#f44336" }} />}
          cantidad={porCategoria.sinMatch.cantidad}
          movimientos={clasificadosFiltrados.sinMatch}
          bgColor="#FFEBEE"
          expanded={expandedSections.sinMatch}
          onToggle={() => toggleSection("sinMatch")}
          pagination={pagination.sinMatch}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          showActions={true}
          categoria="sinMatch"
          onVerSugerencias={handleVerSugerencias}
          onConciliarManual={handleConciliarManual}
          onConciliarIndividual={(detalle) =>
            handleConciliarIndividual(detalle, onClose)
          }
          procesandoAccion={procesandoAccion}
          movimientosEnProceso={movimientosEnProceso}
        />

        {/* Tabla Errores (solo si existen) */}
        {porCategoria.errores.cantidad > 0 && (
          <TablaMovimientos
            sectionKey="errores"
            titulo="⚪ Errores"
            icono={<ErrorIcon style={{ color: "#9e9e9e" }} />}
            cantidad={porCategoria.errores.cantidad}
            movimientos={clasificadosFiltrados.errores}
            bgColor="#F5F5F5"
            expanded={expandedSections.errores}
            onToggle={() => toggleSection("errores")}
            pagination={pagination.errores}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            showActions={false}
            categoria="errores"
            onVerSugerencias={handleVerSugerencias}
            onConciliarManual={handleConciliarManual}
            onConciliarIndividual={(detalle) =>
              handleConciliarIndividual(detalle, onClose)
            }
            procesandoAccion={procesandoAccion}
            movimientosEnProceso={movimientosEnProceso}
          />
        )}
      </DialogContent>

      <DialogActions style={{ justifyContent: "space-between", padding: 16 }}>
        {/* Botones de acción masiva (izquierda) */}
        <Box display="flex" gap={1}>
          {modoMarchaBlanca && porCategoria.perfectos.cantidad > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircleIcon />}
              onClick={handleConciliarPerfectos}
              disabled={procesandoAccion}
            >
              Conciliar Perfectos ({porCategoria.perfectos.cantidad})
            </Button>
          )}

          {porCategoria.buenos.cantidad > 0 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<WarningIcon />}
              onClick={() => toggleSection("buenos")}
              disabled={procesandoAccion}
            >
              Revisar Buenos ({porCategoria.buenos.cantidad})
            </Button>
          )}
        </Box>

        {/* Botón cerrar (derecha) */}
        <Button
          onClick={onClose}
          variant="contained"
          disabled={procesandoAccion}
        >
          Cerrar
        </Button>
      </DialogActions>

      {/* ==================== MODALES AUXILIARES ==================== */}
      {/* Modal de Sugerencias */}
      <SugerenciasModal
        open={modalSugerenciasOpen.open}
        onClose={handleCloseSugerencias}
        movimiento={modalSugerenciasOpen.movimiento}
        onConciliarManual={handleConciliarManual}
        onSuccess={handleConciliacionExitosaDesdeModal}
      />
      {/* Modal de Conciliación Manual */}
      <ConciliacionManualModal
        open={modalConciliarOpen.open}
        onClose={handleCloseConciliarManual}
        movimiento={modalConciliarOpen.movimiento}
        onSuccess={handleConciliacionExitosaDesdeModal}
      />
    </Dialog>
  );
}
